/**
 * The product-event catalogue: every event the clients may emit, and the fields each
 * one carries.
 *
 * This is the single source of truth, and `pnpm analytics` checks it against the
 * `**Analytics**` lines in docs/04-screen-specs.md in both directions — an event in the
 * spec and not here is unimplementable, and one here and not in the spec is untraceable
 * to a screen. Checking the two found three defects in the spec on the first run: one
 * event spelled `words_game_start` on one screen and `word_game_start` on another, a
 * value enum written where a field name belongs, and the north-star metric
 * `time_to_first_utterance_ms` with no stated payload.
 *
 * Fields are named but their value types are not narrowed past {@link AnalyticsValue}.
 * That is deliberate rather than lazy: Analytics Engine stores a data point as blobs
 * and doubles, so a scalar is all it can hold anyway, and declaring 400-odd individual
 * field types from prose would be inventing precision the spec does not have. What the
 * catalogue *does* enforce is exact: the event must exist, and the payload must carry
 * exactly its declared fields — a missing or misspelled field is a compile error at the
 * call site on every platform.
 */

import { z } from 'zod';

/**
 * What a field may hold. Scalars map onto an Analytics Engine data point directly; an
 * array or a record of numbers is serialised to one JSON blob, which is how the fields
 * the spec writes as aggregates (`scores`, `goals`, `filters`) are carried.
 *
 * The schema is the declaration and the type is inferred from it, rather than the two
 * being written out side by side. They had already drifted once when they were: the
 * hand-written type said `readonly (string | number)[]` and the schema inferred a
 * mutable array, so the real client did not satisfy the interface built from it.
 */
export const AnalyticsValueSchema = z.union([
  z.string().max(512),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(z.union([z.string().max(128), z.number().finite()])).max(32),
  z.record(z.string().max(40), z.number().finite()),
]);

export type AnalyticsValue = z.infer<typeof AnalyticsValueSchema>;

/**
 * Event name → its field names. Ordered, because the blob and double positions in an
 * Analytics Engine data point are derived from this order and a reordering would make
 * historical rows unreadable. Append to an event's fields; never reorder them.
 */
export const ANALYTICS_EVENTS = {
  account_delete_requested: [],
  achievement_unlocked: ['key', 'tier'],
  achievements_view: ['unlocked', 'total'],
  assessment_complete: ['level_before', 'level_after', 'confidence'],
  assessment_start: [],
  assessment_view: ['state'],
  cancel_flow_entered: [],
  cefr_detail_view: ['level', 'confidence', 'band_sublevels', 'source'],
  cefr_evidence_expand: ['component'],
  cefr_retest_tap: ['available'],
  certificate_generate: ['level'],
  certificate_share: ['destination'],
  certificate_view: ['level', 'earned'],
  challenge_complete: ['kind', 'correct'],
  challenge_start: ['kind'],
  challenge_view: ['state'],
  clinic_drill: ['phoneme', 'before', 'after'],
  clinic_view: ['weak_phonemes'],
  concept_complete: ['accuracy'],
  concept_view: ['id'],
  conjugation_session: ['verb', 'tense', 'accuracy', 'mode'],
  /**
   * A learner said an item is wrong. `reason` is the dispute reason and `surface` is
   * where they were when they said it: a reason that spikes on one surface is a bug in
   * that screen, not in the corpus.
   */
  content_reported: ['item', 'reason', 'surface'],
  contextual_paywall_convert: ['capability', 'product_id'],
  contextual_paywall_dismiss: ['capability'],
  contextual_paywall_view: ['capability', 'trigger'],
  conversation_report_view: ['scores', 'criteria_met'],
  course_path_view: ['module_count', 'current'],
  data_export_requested: [],
  debate_end: ['turns', 'seconds'],
  debate_start: ['topic', 'side'],
  debate_turn: ['index', 'rebuttal_quality'],
  drill_complete: ['accuracy'],
  drill_item: ['type', 'correct', 'ms'],
  drill_start: ['source_lesson', 'item_count'],
  feedback_appeal: ['word', 'phoneme'],
  feedback_card_view: ['score'],
  feedback_compare_tapped: [],
  feedback_explain_tapped: [],
  feedback_phoneme_audio: ['phoneme'],
  feedback_reported: ['reason'],
  feedback_word_expanded: ['word', 'score'],
  grammar_clinic_tapped: ['top_error'],
  grammar_home_view: ['mastery_map'],
  improved_speech_played: [],
  interview_end: ['turns', 'seconds'],
  interview_question: ['index', 'competency'],
  interview_start: ['track'],
  league_demoted: ['from', 'to'],
  league_promoted: ['from', 'to'],
  league_view: ['rank', 'members', 'zone'],
  learn_home_view: ['scores'],
  learn_pillar_tapped: ['pillar'],
  lesson_abandoned: ['turn', 'reason'],
  lesson_complete: ['id', 'scores', 'duration_ms', 'turns', 'mistakes', 'hints'],
  lesson_degraded: ['reason'],
  lesson_intro_view: ['id', 'cefr'],
  lesson_node_tapped: ['id', 'state'],
  lesson_start: ['id', 'mode', 'scaffolding', 'persona'],
  lesson_turn_complete: [
    'turn',
    'score',
    'confidence',
    'hint_used',
    'translate_used',
    'retries',
    'latency_ms',
  ],
  /**
   * Asking for help in a live conversation, and whether the help was used.
   *
   * `lesson_turn_complete` has carried `hint_used` and `translate_used` from the start,
   * but a scripted lesson is not where a learner runs dry — an open conversation is,
   * and there the request had nowhere to go and nothing to report.
   */
  /**
   * Hiding the tutor's words to practise listening, or showing them again.
   *
   * Worth measuring rather than assuming: if nobody ever hides them, reading along is
   * what the product actually is, and the speaking-first claim needs re-examining.
   */
  live_transcript_toggle: ['visible'],
  live_hint_request: ['kind'],
  /** Asking the tutor to say it again, or to say it slowly. */
  live_repeat_request: ['kind'],
  live_slow_request: ['kind'],
  live_hint_use: ['kind'],
  live_translate_request: ['kind'],
  library_filter: ['facet', 'value'],
  library_item_open: ['kind', 'id'],
  library_view: ['items', 'filters'],
  limit_alternative_tap: ['capability', 'alternative'],
  limit_sheet_upgrade_tap: ['capability'],
  limit_sheet_view: ['capability', 'alternatives'],
  listen_home_view: ['level', 'items'],
  listen_item_play: ['id', 'speed'],
  listen_quiz_complete: ['accuracy'],
  listen_shadow: ['score'],
  listen_word_added: ['item'],
  live_criteria_met: ['criterion'],
  live_session_end: ['duration_s', 'turns', 'reason'],
  live_session_start: ['mode', 'scenario'],
  live_session_turn: ['n', 'score', 'latency_ms'],
  mistake_closed: ['type', 'attempts'],
  mistake_list_view: ['open', 'closed'],
  mistake_practise: ['type', 'item'],
  onboarding_ab_playback: ['which'],
  onboarding_anxiety_ack_continue: ['chose_text_mode'],
  onboarding_anxiety_ack_view: ['signal'],
  onboarding_carousel_skip: ['at_card'],
  onboarding_carousel_view: ['card'],
  onboarding_complete: ['duration_ms', 'steps_shown', 'cefr'],
  onboarding_conversational_quiz_start: ['persona'],
  onboarding_dialect_preview: ['dialect'],
  onboarding_dialect_select: ['dialect'],
  onboarding_first_action: ['action'],
  onboarding_first_score: ['accuracy', 'band', 'confidence'],
  onboarding_goal_set: ['minutes', 'reminder_local_time'],
  onboarding_language_requested: ['text'],
  onboarding_language_select: ['lang'],
  onboarding_mic_permission: ['granted'],
  onboarding_mic_prompt_shown: [],
  onboarding_motivation_select: ['goals', 'exam'],
  onboarding_notif_permission: ['granted'],
  onboarding_notif_warmup_view: [],
  /**
   * The plan-building moment: the quiz answers shown being turned into a plan.
   *
   * Learna ($6M/month) scores highest in the category on exactly this screen — it is
   * what makes the preceding questions feel answered rather than merely asked.
   */
  onboarding_plan_built: ['items', 'ms'],
  onboarding_placement_complete: ['cefr', 'confidence', 'vocab_estimate', 'duration_ms'],
  onboarding_quiz_fallback: ['reason'],
  onboarding_quiz_turn: ['index', 'answered_by'],
  /**
   * What went wrong with the app they tried before, and which promise we led with.
   *
   * The distribution is the thing to watch: if `wanted_money_first` dominates, the free
   * tier is the product, and if `marked_me_wrong` does, confidence gating is.
   */
  onboarding_prior_app: ['failure', 'promise'],
  onboarding_retry: [],
  onboarding_score_reveal: ['band'],
  onboarding_step_view: ['step'],
  onboarding_tutor_preview: ['persona'],
  onboarding_tutor_select: ['persona', 'strictness', 'correction_style'],
  onboarding_utterance_recorded: ['ms', 'snr'],
  open_talk_end: ['turns', 'seconds', 'subject_changes'],
  open_talk_start: ['opener_source'],
  open_talk_turn: ['index', 'stalled'],
  paywall_cta: ['plan'],
  paywall_dismiss: ['trigger', 'ms_visible'],
  /**
   * The offer made when a paywall is dismissed: one free lesson, no card.
   *
   * Loora's version of this is the highest-scoring dismissal handling in the set. It
   * is recorded separately from the paywall's own numbers because it answers a
   * different question — whether someone who said no will still try the product.
   */
  paywall_dismiss_offer: ['accepted'],
  paywall_plans_expanded: [],
  paywall_view: ['trigger', 'variant', 'plan_shown'],
  placement_retest_complete: ['old_cefr', 'new_cefr', 'confidence'],
  plan_comparison_view: ['source', 'current_tier'],
  plan_selected: ['product_id', 'period'],
  progress_view: ['cefr', 'scores', 'spoken_minutes_7d'],
  refund_requested: [],
  report_card_drill_tapped: [],
  report_card_transcript_tapped: [],
  report_open: ['session_id', 'age_days'],
  report_retry_harder: [],
  reports_truncation_prompt_view: [],
  reports_view: ['count', 'truncated_by_tier'],
  review_card: ['item', 'modality', 'grade', 'ms'],
  review_home_view: ['due', 'mistakes', 'leeches'],
  review_session_complete: ['count', 'accuracy', 'duration_ms'],
  roleplay_custom_created: ['prompt_len'],
  roleplay_rail_tapped: ['rail'],
  roleplay_surprise: [],
  saved_item_add: ['source'],
  saved_export: ['format'],
  saved_items_view: [],
  scenario_card_tapped: ['id', 'locked'],
  scenario_detail_view: ['id'],
  scenario_library_view: ['filters'],
  scenario_search: ['query', 'results'],
  /**
   * The share loop, measured at each hop: the author creates a link, someone opens it,
   * that person starts practising. Two of the three happen to people who may have no
   * account, which is the only way to see whether sharing brings anybody in.
   */
  scenario_shared: ['id'],
  scenario_unshared: ['id'],
  scenario_start: ['id', 'mode'],
  shared_scenario_start: ['token'],
  shared_scenario_view: ['token'],
  settings_changed: ['key', 'value'],
  streak_detail_view: [],
  streak_share: ['streak'],
  subscription_welcome_dismiss: ['entitlement_ready'],
  subscription_welcome_view: ['tier', 'period'],
  swipe_card: ['item', 'direction', 'flipped', 'ms'],
  swipe_deck_complete: ['count', 'known_pct'],
  talk_home_view: ['minutes_left'],
  talk_mode_tapped: ['mode'],
  texting_session: ['scenario', 'turns', 'corrections'],
  time_to_first_utterance_ms: ['ms'],
  today_focus_tapped: ['focus_type'],
  today_next_up_rejected: [],
  today_next_up_shown: ['type', 'id', 'rank_reason'],
  today_next_up_tapped: [],
  today_view: ['goal_pct', 'due_count', 'streak', 'minutes_left'],
  tour_complete: [],
  tour_skip: ['at_index'],
  tour_start: [],
  tour_step: ['index'],
  trial_timeline_back: [],
  trial_timeline_continue: ['product_id'],
  trial_timeline_view: ['product_id', 'trial_days'],
  tutor_changed: ['from', 'to', 'scope'],
  usage_sheet_view: ['used', 'limit', 'trigger'],
  voice_minutes_consumed: ['seconds'],
  winback_convert: ['offer'],
  winback_dismiss: [],
  winback_view: ['lapsed_days', 'offer'],
  word_detail_view: ['item', 'source'],
  word_game_complete: ['type', 'correct', 'total', 'items_scheduled'],
  word_game_start: ['type', 'seed'],
  word_practise_tapped: [],
  /**
   * The workbook had no measurement at all: a built, routed screen nobody could tell was
   * being used. Its spec was reported thin only after the spec gate stopped letting the
   * last screen in each group borrow the next group's states and analytics.
   */
  workbook_practise_tapped: ['severity'],
  workbook_view: ['open_mistakes'],
  words_home_view: ['new', 'learning', 'mastered', 'due'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

/** The payload for one event: exactly its declared fields, no more and no fewer. */
export type AnalyticsPayload<K extends AnalyticsEventName> = {
  readonly [F in (typeof ANALYTICS_EVENTS)[K][number]]: AnalyticsValue;
};

/**
 * Analytics Engine caps a data point at 20 blobs and 20 doubles. No event in the
 * catalogue is near it, but the cap is a hard runtime failure rather than a validation
 * error, so it is asserted in the test suite rather than discovered in production.
 */
export const AE_MAX_BLOBS = 20;
export const AE_MAX_DOUBLES = 20;

export const ANALYTICS_EVENT_NAMES = Object.keys(ANALYTICS_EVENTS) as readonly AnalyticsEventName[];

/** True when `name` is an event the catalogue declares. */
export function isAnalyticsEventName(name: string): name is AnalyticsEventName {
  return Object.hasOwn(ANALYTICS_EVENTS, name);
}

/** The declared fields for an event, or undefined when the name is not in the catalogue. */
export function analyticsFields(name: string): readonly string[] | undefined {
  return isAnalyticsEventName(name) ? ANALYTICS_EVENTS[name] : undefined;
}

export interface EventValidationResult {
  readonly ok: boolean;
  /** Human-readable, and safe to log: field names only, never values. */
  readonly reason: string | null;
}

/**
 * Checks one event against the catalogue: the name must be declared, and the payload
 * must carry exactly the declared fields.
 *
 * "Exactly" is the strict reading on purpose. A missing field is a measurement that
 * will read as a null on a dashboard rather than as a bug, and an extra one is a
 * client sending data no screen documents — the two failures this whole catalogue
 * exists to make impossible.
 */
export function validateEvent(
  name: string,
  payload: Readonly<Record<string, unknown>>,
): EventValidationResult {
  const declared = analyticsFields(name);
  if (declared === undefined) return { ok: false, reason: 'not in the catalogue' };

  const got = Object.keys(payload);
  const missing = declared.filter((field) => !Object.hasOwn(payload, field));
  const extra = got.filter((field) => !declared.includes(field as never));

  if (missing.length > 0) return { ok: false, reason: `missing field(s): ${missing.join(', ')}` };
  if (extra.length > 0) return { ok: false, reason: `undeclared field(s): ${extra.join(', ')}` };
  return { ok: true, reason: null };
}

/**
 * Flattens a payload into an Analytics Engine data point.
 *
 * Field order follows the catalogue, not the object's key order, because blob and
 * double positions are how the rows are read back and an object's key order is an
 * accident of however the client built it. Strings go to blobs and numbers to doubles;
 * a boolean becomes a double (1/0) so it can be averaged, which is what a rate is; an
 * array or record becomes one JSON blob.
 */
export function toDataPoint(
  name: AnalyticsEventName,
  payload: Readonly<Record<string, AnalyticsValue>>,
): { blobs: string[]; doubles: number[]; indexes: [string] } {
  const blobs: string[] = [name];
  const doubles: number[] = [];

  for (const field of ANALYTICS_EVENTS[name] as readonly string[]) {
    const value = payload[field];
    if (typeof value === 'number') doubles.push(value);
    else if (typeof value === 'boolean') doubles.push(value ? 1 : 0);
    else if (typeof value === 'string') blobs.push(value);
    else if (value === null || value === undefined) blobs.push('');
    else blobs.push(JSON.stringify(value));
  }

  // The index is what Analytics Engine groups by, and it is the event name: every
  // question anyone asks of this data starts with "how often did X happen".
  return { blobs, doubles, indexes: [name] };
}
